import type { CoefficientDTO, SurchargeDTO } from '@/shared/api'

// Подписи типов условий фиксированных надбавок
export const CONDITION_LABELS: Record<number, string> = {
  0: 'Всегда',
  1: 'Тип оплаты',
  2: 'Приоритет',
  3: 'Число товаров-мест',
}

// Подписи типов правил коэффициентов
export const COEF_LABELS: Record<number, string> = {
  0: 'Постоянный',
  1: 'Время',
  2: 'День недели',
  3: 'Точная дата',
  4: 'Диапазон дат',
}

// Периоды надбавок за количество заказов
export const PERIOD_LABELS: Record<number, string> = {
  1: 'День',
  2: 'Неделя',
  3: 'Месяц',
}

// Дни недели для коэффициентов (Пн=1 … Вс=7)
export const WEEKDAY_LABELS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']

// Секунды от начала суток ↔ строка HH:MM
export const secondsToTime = (value: number | null): string => {
  if (value === null || value === undefined) return ''
  const hours = Math.floor(value / 3600)
  const minutes = Math.floor((value % 3600) / 60)
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
}

// Разбор CSV дней недели (например, "1,5")
export const parseWeekdays = (value: string | null): number[] =>
  (value ?? '')
    .split(',')
    .map((part) => Number(part.trim()))
    .filter((day) => day >= 1 && day <= 7)

// Неразрывный пробел: «300 ₽» не должно разрываться между числом и знаком валюты.
const NBSP = '\u00A0'

// Прочерк для пустых значений — единый вид во всех форматах ниже
export const DASH = '—'

export const formatNumber = (value: number, maximumFractionDigits = 2): string =>
  value.toLocaleString('ru-RU', { maximumFractionDigits })

// Деньги: «1 250 ₽», пустое значение — прочерк
export const formatMoney = (value: number | null | undefined): string =>
  value === null || value === undefined ? DASH : `${formatNumber(value)}${NBSP}₽`

// Надбавка: положительные суммы показываем со знаком плюс, отрицательные — как есть
export const formatSignedMoney = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return DASH
  return value > 0 ? `+${formatMoney(value)}` : formatMoney(value)
}

// Коэффициент: «×1,4», до трёх знаков после запятой
export const formatCoefficient = (value: number | null | undefined): string =>
  value === null || value === undefined
    ? DASH
    : `×${value.toLocaleString('ru-RU', { maximumFractionDigits: 3 })}`

// Цена за километр: «30 ₽/км»
export const formatPricePerKm = (value: number | null | undefined): string =>
  value === null || value === undefined ? DASH : `${formatNumber(value)}${NBSP}₽/км`

// Километраж: «10 км»
export const formatKm = (value: number | null | undefined): string =>
  value === null || value === undefined ? DASH : `${formatNumber(value)}${NBSP}км`

export const periodLabel = (period: number): string => PERIOD_LABELS[period] ?? DASH

// Дата правила коэффициента без года: правила ежегодные, поэтому год не показываем
const monthDayText = (month: number | null, day: number | null): string =>
  month && day
    ? `${String(day).padStart(2, '0')}.${String(month).padStart(2, '0')}`
    : DASH

// Условия применения коэффициента человеческим языком
export const coefficientConditionText = (coefficient: CoefficientDTO): string => {
  switch (coefficient.condition_type) {
    case 0:
      return 'Действует постоянно'
    case 1: {
      const from = secondsToTime(coefficient.time_from)
      const to = secondsToTime(coefficient.time_to)
      // Если границы не заданы вовсе — не рисуем «— — —», достаточно одного прочерка
      if (!from && !to) return `Время: ${DASH}`
      return `Время: ${from || DASH} — ${to || DASH}`
    }
    case 2: {
      const days = parseWeekdays(coefficient.weekdays).map((day) => WEEKDAY_LABELS[day - 1])
      return `Дни недели: ${days.length ? days.join(', ') : DASH}`
    }
    case 3:
      return `Дата: ${monthDayText(coefficient.date_start_month, coefficient.date_start_day)} (ежегодно)`
    case 4: {
      const start = monthDayText(coefficient.date_start_month, coefficient.date_start_day)
      const end = monthDayText(coefficient.date_end_month, coefficient.date_end_day)
      if (start === DASH && end === DASH) return `Период: ${DASH} (ежегодно)`
      return `Период: ${start} — ${end} (ежегодно)`
    }
    default:
      return DASH
  }
}

// Условия применения фиксированной надбавки человеческим языком
export const surchargeConditionText = (surcharge: SurchargeDTO): string => {
  const value = surcharge.condition_value?.trim() ? surcharge.condition_value : DASH
  switch (surcharge.condition_type) {
    case 0:
      return 'Без дополнительных условий'
    case 1:
      return `Тип оплаты: ${value}`
    case 2:
      return `Приоритет: ${value}`
    case 3:
      return `Товаров-мест: ${value}`
    default:
      return DASH
  }
}
